import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useMachineOrderStore } from "@/shared/stores/useMachineOrderStore";
import { Machine } from "@/machines/types/machine";
import { useTranslation } from "react-i18next";

export const useLocationMachinesOrder = (locationId: number, machines?: Machine[]) => {
  const { t } = useTranslation("locations");
  const { setOrder, moveInOrder } = useMachineOrderStore.getState();
  const [order, setOrderState] = useState<string[] | undefined>(
    () => useMachineOrderStore.getState().locationIdToOrder[String(locationId)]
  );
  useEffect(() => {
    // set initial snapshot for this location
    setOrderState(useMachineOrderStore.getState().locationIdToOrder[String(locationId)]);
    const unsubscribe = useMachineOrderStore.subscribe((s) => {
      setOrderState(s.locationIdToOrder[String(locationId)]);
    });
    return () => unsubscribe();
  }, [locationId]);
  const [orderMode, setOrderMode] = useState(false);
  const dragSourceIdRef = useRef<string | null>(null);
  const initializedRef = useRef(false);

  // Initialize order when machines load and no order exists yet
  useEffect(() => {
    if (initializedRef.current) return;
    if (!machines || machines.length === 0) return;
    if (!order || order.length === 0) {
      setOrder(locationId, machines.map((m) => String(m.id)));
      initializedRef.current = true;
    }
  }, [machines, order, locationId, setOrder]);

  const orderedMachines = useMemo(() => {
    const list = machines ?? [];
    const existing = order;
    if (!existing || existing.length === 0) {
      return list;
    }
    const idToMachine = new Map(list.map((m) => [String(m.id), m]));
    const ordered = existing.map((id) => idToMachine.get(id)).filter(Boolean) as Machine[];
    const remaining = list.filter((m) => !existing.includes(String(m.id)));
    return [...ordered, ...remaining];
  }, [machines, order]);

  const onDragStart = (idStr: string) => {
    dragSourceIdRef.current = idStr;
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const onDrop = (targetIdStr: string) => {
    const from = dragSourceIdRef.current;
    if (!from || from === targetIdStr) return;
    moveInOrder(locationId, from, targetIdStr);
    dragSourceIdRef.current = null;
  };

  const toggleOrderMode = () => setOrderMode((v) => !v);

  const getDraggableProps = useCallback((id: string) => {
    if (!orderMode) {
      return {
        draggable: false,
        onDoubleClick: toggleOrderMode,
        title: t("order.enterHint"),
      } as const;
    }
    return {
      draggable: true,
      onDragStart: () => onDragStart(id),
      onDragOver: onDragOver,
      onDrop: () => onDrop(id),
      onDoubleClick: toggleOrderMode,
      className: "cursor-move border border-dashed border-border rounded-lg",
      title: t("order.exitHint"),
    } as const;
  }, [orderMode, t]);

  return {
    orderMode,
    toggleOrderMode,
    orderedMachines,
    getDraggableProps,
  };
};


